/** A GraphQL `*Collection` page used by Advisors list/grid load-more. */
export type CollectionPage<TItem = unknown> = {
  items?: Array<TItem | null> | null
  pagination?: {
    hasNextPage?: boolean | null
    itemCount?: number | null
    page?: number | null
    pageCount?: number | null
    pageSize?: number | null
  } | null
}

const UNIQUE_KEYS = [
  'advisorSessionId',
  'advisorEngagementId',
  'advisorProfileId',
  'advisorAgreementId',
  'advisorPayoutId',
  'advisorLedgerEntryId',
  'userMatchId',
  'groupCommitmentId',
  'toolSessionId',
  'userId',
  'id',
] as const

export const collectionItemKey = (item: unknown): string | null => {
  if (!item || typeof item !== 'object') {
    return null
  }
  const record = item as Record<string, unknown>
  for (const field of UNIQUE_KEYS) {
    const value = record[field]
    if (value != null && value !== '') {
      return `${field}:${String(value)}`
    }
  }
  return null
}

/** Append an incoming collection page onto the already-loaded items, de-duped. */
export const mergeCollectionPage = <T extends CollectionPage>(
  existing: T | null | undefined,
  incoming: T | null | undefined
): T | null | undefined => {
  if (!incoming) {
    return existing
  }
  if (!existing) {
    return incoming
  }
  const seen = new Set<string>()
  const items: Array<unknown> = []
  for (const item of [...(existing.items ?? []), ...(incoming.items ?? [])]) {
    const key = collectionItemKey(item)
    if (key) {
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
    }
    items.push(item)
  }
  return { ...incoming, items }
}
